<template>
  <div class="qr-scanner">
    <!-- 扫码弹窗 -->
    <van-popup
      v-model="showScanner"
      position="center"
      :style="{ width: '90%', height: '70%' }"
      closeable
      close-on-click-overlay
      @close="closeScanner"
    >
      <div class="scanner-container">
        <div class="scanner-header">
          <h3>扫描二维码</h3>
          <p>请将二维码放入框内进行扫描</p>
        </div>

        <div class="scanner-content">
          <!-- 摄像头视频流 -->
          <div class="video-container">
            <video
              ref="video"
              :width="videoWidth"
              :height="videoHeight"
              autoplay
              muted
              playsinline
            ></video>

            <!-- 扫描框 -->
            <div class="scan-box">
              <div class="scan-line"></div>
            </div>

            <!-- Canvas用于处理图像 -->
            <canvas
              ref="canvas"
              :width="videoWidth"
              :height="videoHeight"
              style="display: none"
            ></canvas>
          </div>

          <!-- 状态提示 -->
          <div class="scanner-status">
            <p v-if="scanning">{{ statusText }}</p>
            <p v-if="!cameraSupported" class="error">
              您的设备不支持摄像头功能
            </p>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="scanner-footer">
          <van-button type="default" size="small" @click="closeScanner">
            取消
          </van-button>
          <van-button
            type="primary"
            size="small"
            @click="toggleFlashlight"
            v-if="flashlightSupported"
          >
            {{ flashlightOn ? "关闭" : "开启" }}闪光灯
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import jsQR from 'jsqr'

export default {
  name: 'QrScanner',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showScanner: false,
      scanning: false,
      stream: null,
      videoWidth: 300,
      videoHeight: 300,
      cameraSupported: true,
      flashlightSupported: false,
      flashlightOn: false,
      statusText: '正在启动摄像头...',
      scanInterval: null
    }
  },
  watch: {
    show (newVal) {
      this.showScanner = newVal
      if (newVal) {
        this.startScanner()
      } else {
        this.stopScanner()
      }
    },
    showScanner (newVal) {
      if (!newVal) {
        this.stopScanner()
        this.$emit('close')
      }
    }
  },
  methods: {
    async startScanner () {
      try {
        this.scanning = true
        this.statusText = '正在启动摄像头...'

        // 检查浏览器是否支持getUserMedia
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          this.cameraSupported = false
          this.statusText = '您的浏览器不支持摄像头功能'
          return
        }

        // 请求摄像头权限
        const constraints = {
          video: {
            facingMode: 'environment', // 后置摄像头
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        }

        this.stream = await navigator.mediaDevices.getUserMedia(constraints)

        // 将视频流绑定到video元素
        this.$refs.video.srcObject = this.stream

        // 等待视频加载完成
        this.$refs.video.addEventListener('loadedmetadata', () => {
          this.statusText = '请将二维码对准扫描框'
          this.startScanLoop()
        })

        // 检查是否支持闪光灯
        this.checkFlashlightSupport()

      } catch (error) {
        console.error('启动摄像头失败:', error)
        this.cameraSupported = false
        this.statusText = '无法访问摄像头，请检查权限设置'
        this.$toast('无法访问摄像头，请检查权限设置')
      }
    },

    startScanLoop () {
      this.scanInterval = setInterval(() => {
        this.scanQRCode()
      }, 300) // 每300ms扫描一次
    },

    scanQRCode () {
      const video = this.$refs.video
      const canvas = this.$refs.canvas
      const context = canvas.getContext('2d')

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        // 将视频帧绘制到canvas上
        context.drawImage(video, 0, 0, this.videoWidth, this.videoHeight)

        // 获取图像数据
        const imageData = context.getImageData(0, 0, this.videoWidth, this.videoHeight)

        // 使用jsQR解析二维码
        const code = jsQR(imageData.data, imageData.width, imageData.height)

        if (code) {
          this.onScanSuccess(code.data)
        }
      }
    },

    onScanSuccess (result) {
      console.log('扫码成功:', result)

      // 停止扫描
      this.stopScanner()

      // 发送扫码结果给父组件
      this.$emit('scan-success', result)
    },

    stopScanner () {
      this.scanning = false

      // 清除扫描循环
      if (this.scanInterval) {
        clearInterval(this.scanInterval)
        this.scanInterval = null
      }

      // 停止视频流
      if (this.stream) {
        this.stream.getTracks().forEach(track => {
          track.stop()
        })
        this.stream = null
      }

      // 清空video元素
      if (this.$refs.video) {
        this.$refs.video.srcObject = null
      }
    },

    closeScanner () {
      this.showScanner = false
    },

    async checkFlashlightSupport () {
      try {
        if (this.stream) {
          const track = this.stream.getVideoTracks()[0]
          const capabilities = track.getCapabilities()
          this.flashlightSupported = capabilities.torch === true
        }
      } catch (error) {
        console.log('不支持闪光灯功能')
        this.flashlightSupported = false
      }
    },

    async toggleFlashlight () {
      try {
        if (this.stream) {
          const track = this.stream.getVideoTracks()[0]
          await track.applyConstraints({
            advanced: [{ torch: !this.flashlightOn }]
          })
          this.flashlightOn = !this.flashlightOn
        }
      } catch (error) {
        console.error('切换闪光灯失败:', error)
        this.$toast('闪光灯切换失败')
      }
    }
  },

  beforeDestroy () {
    this.stopScanner()
  }
}
</script>

<style lang="scss" scoped>
.qr-scanner {
  .scanner-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #fff;
  }

  .scanner-header {
    padding: 20px;
    text-align: center;
    border-bottom: 1px solid #eee;

    h3 {
      margin: 0 0 10px 0;
      color: #333;
      font-size: 18px;
    }

    p {
      margin: 0;
      color: #666;
      font-size: 14px;
    }
  }

  .scanner-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }

  .video-container {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

    video {
      display: block;
      border-radius: 8px;
    }
  }

  .scan-box {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200px;
    height: 200px;
    border: 2px solid #1989fa;
    border-radius: 8px;

    &::before,
    &::after {
      content: "";
      position: absolute;
      width: 20px;
      height: 20px;
      border: 3px solid #1989fa;
    }

    &::before {
      top: -3px;
      left: -3px;
      border-right: none;
      border-bottom: none;
    }

    &::after {
      bottom: -3px;
      right: -3px;
      border-left: none;
      border-top: none;
    }
  }

  .scan-line {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #1989fa, transparent);
    animation: scan 2s linear infinite;
  }

  @keyframes scan {
    0% {
      top: 0;
    }
    100% {
      top: 100%;
    }
  }

  .scanner-status {
    margin-top: 20px;
    text-align: center;

    p {
      margin: 0;
      color: #666;
      font-size: 14px;

      &.error {
        color: #f44;
      }
    }
  }

  .scanner-footer {
    padding: 20px;
    display: flex;
    justify-content: space-around;
    border-top: 1px solid #eee;

    .van-button {
      min-width: 80px;
    }
  }
}
</style>
